import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { Bell, Building2, CheckCheck, ChevronDown, ChevronRight, LogOut, Menu, Search, X } from 'lucide-react'
import { notificationsApi, settingsApi } from '../api/services.js'
import {
  resolveAdminRoute,
  searchableAdminPages,
  sidebarAdminGroups,
} from '../config/adminNavigation.js'
import { getRoleLabel } from '../constants/roles.js'
import { useAuth } from '../hooks/useAuth.js'
import { useFeatureEnabled } from '../hooks/useSchoolFeatures.js'
import {
  appendReadByUser,
  getEntityId,
  getNotificationId,
  isNotificationReadByUser,
  markNotificationReadByUser,
  markNotificationsReadByUser,
  normalizeNotificationList,
  removeNotificationById,
  upsertNotification,
} from '../utils/notifications.js'
import { connectMessageSocket, disconnectMessageSocket } from '../services/messageSocket.js'
import Avatar from './Avatar.jsx'

const matchesRoute = (pathname, path, aliases = []) => [path, ...aliases].includes(pathname)

const getInitials = (value) => (
  value
    ?.split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
  || 'BC'
)

const formatNotificationTime = (value) => {
  if (!value) return ''

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const diffMs = date.getTime() - Date.now()
  const diffMinutes = Math.round(diffMs / 60000)

  if (Math.abs(diffMinutes) < 60) {
    return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(diffMinutes, 'minute')
  }

  const diffHours = Math.round(diffMinutes / 60)
  if (Math.abs(diffHours) < 24) {
    return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(diffHours, 'hour')
  }

  const diffDays = Math.round(diffHours / 24)
  if (Math.abs(diffDays) < 7) {
    return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(diffDays, 'day')
  }

  return new Intl.DateTimeFormat('en-IN', {
    day: 'numeric',
    month: 'short',
  }).format(date)
}

const getNotificationAccent = (notification) => {
  const targetRole = (notification?.targetRole || '').toLowerCase()

  if (targetRole === 'student') return 'bg-green-500'
  if (targetRole === 'teacher') return 'bg-cyan-500'
  if (targetRole === 'parent') return 'bg-amber-500'
  if (targetRole === 'admin') return 'bg-violet-500'
  return 'bg-blue-500'
}

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [notificationsLoading, setNotificationsLoading] = useState(false)
  const [collapsedGroups, setCollapsedGroups] = useState({})
  const [quickQuery, setQuickQuery] = useState('')
  const [quickOpen, setQuickOpen] = useState(false)
  const [schoolName, setSchoolName] = useState('')
  const [schoolCodeValue, setSchoolCodeValue] = useState('')
  const navigate = useNavigate()
  const location = useLocation()
  const searchInputRef = useRef(null)
  const { user, schoolConfig, logout, token } = useAuth()
  const feesEnabled = useFeatureEnabled('fees')
  const currentUserId = getEntityId(user)

  const normalizedSchoolId = user?.school?._id
    || user?.schoolId?._id
    || user?.schoolId?.id
    || user?.schoolId
    || user?.school
    || ''

  const derivedSchoolName = schoolConfig?.appName || user?.school?.name || user?.schoolId?.name || user?.schoolName || ''
  const schoolLogo = schoolConfig?.theme?.logoUrl || schoolConfig?.logoUrl || null
  const derivedSchoolCode = schoolConfig?.schoolCode || user?.school?.schoolCode || user?.schoolId?.schoolCode || ''
  const schoolDisplayName = schoolName || schoolConfig?.appName || 'School Admin Workspace'
  const schoolInitials = getInitials(schoolDisplayName)
  const currentRoute = resolveAdminRoute(location.pathname)
  const pageTitle = currentRoute?.title || 'BrainCamp Admin'
  const pageDescription = currentRoute?.description || 'Manage your school operations through the admin workspace.'

  const searchablePages = useMemo(
    () => searchableAdminPages
      .filter((page) => !page.key?.startsWith('fee') || feesEnabled)
      .map((page) => ({ ...page, group: page.group || 'Workspace' })),
    [feesEnabled]
  )

  const visibleSidebarGroups = useMemo(
    () => sidebarAdminGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => !item.key?.startsWith('fee') || feesEnabled),
      }))
      .filter((group) => group.items.length > 0),
    [feesEnabled]
  )

  const unreadNotifications = useMemo(
    () => notifications.filter((notification) => !isNotificationReadByUser(notification, currentUserId)),
    [currentUserId, notifications]
  )

  const recentNotifications = useMemo(
    () => notifications.slice(0, 5),
    [notifications]
  )

  const unreadCount = unreadNotifications.length

  const quickResults = useMemo(() => {
    const query = quickQuery.trim().toLowerCase()

    if (!query) return searchablePages.slice(0, 8)

    return searchablePages
      .map((page) => {
        const haystack = [page.label, page.group, page.description].join(' ').toLowerCase()
        let score = 0

        if (page.label.toLowerCase() === query) score += 120
        if (page.label.toLowerCase().startsWith(query)) score += 80
        if (haystack.includes(query)) score += 40
        if (page.group.toLowerCase().includes(query)) score += 10

        return { ...page, score }
      })
      .filter((page) => page.score > 0)
      .sort((left, right) => right.score - left.score || left.label.localeCompare(right.label))
      .slice(0, 8)
  }, [quickQuery, searchablePages])

  const handleLogout = async () => {
    await logout()
    navigate('/login', { replace: true })
  }

  const toggleGroup = (label) => {
    setCollapsedGroups((previous) => ({ ...previous, [label]: !previous[label] }))
  }

  const handleQuickNavigate = (path) => {
    navigate(path)
    setQuickQuery('')
    setQuickOpen(false)
  }

  const handleMarkAllNotificationsRead = async () => {
    if (!currentUserId || unreadCount === 0) return

    try {
      await notificationsApi.markAllRead()
      setNotifications((current) => current.map((notification) => appendReadByUser(notification, currentUserId)))
    } catch {}
  }

  const fetchNotifications = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setNotificationsLoading(true)
      const data = await notificationsApi.getAll()
      setNotifications(normalizeNotificationList(data))
    } catch {
      if (!silent) setNotifications([])
    } finally {
      if (!silent) setNotificationsLoading(false)
    }
  }, [])

  useEffect(() => {
    setNotifOpen(false)
    setQuickOpen(false)
    setQuickQuery('')
  }, [location.pathname])

  useEffect(() => {
    setSchoolName(derivedSchoolName)
    setSchoolCodeValue(derivedSchoolCode)
  }, [derivedSchoolCode, derivedSchoolName])

  useEffect(() => {
    let isMounted = true

    if (!normalizedSchoolId || typeof normalizedSchoolId !== 'string') {
      return undefined
    }

    if (derivedSchoolName && derivedSchoolCode) {
      return undefined
    }

    settingsApi.getSchool(normalizedSchoolId)
      .then((school) => {
        if (!isMounted) return
        setSchoolName(school?.name || '')
        setSchoolCodeValue(school?.schoolCode || '')
      })
      .catch(() => {})

    return () => {
      isMounted = false
    }
  }, [derivedSchoolCode, derivedSchoolName, normalizedSchoolId])

  useEffect(() => {
    const appName = schoolConfig?.appName || schoolName || 'BrainCamp Admin'
    document.title = `${pageTitle} | ${appName}`
  }, [pageTitle, schoolConfig?.appName, schoolName])

  useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications])

  useEffect(() => {
    const refreshOnFocus = () => {
      fetchNotifications({ silent: true })
    }

    const refreshOnVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchNotifications({ silent: true })
      }
    }

    window.addEventListener('focus', refreshOnFocus)
    document.addEventListener('visibilitychange', refreshOnVisible)

    return () => {
      window.removeEventListener('focus', refreshOnFocus)
      document.removeEventListener('visibilitychange', refreshOnVisible)
    }
  }, [fetchNotifications])

  useEffect(() => {
    if (!token || !currentUserId) {
      disconnectMessageSocket()
      return undefined
    }

    const socket = connectMessageSocket(token)
    if (!socket) return undefined

    const sameSchoolEvent = (payload) => (
      !payload?.schoolId
      || !normalizedSchoolId
      || String(payload.schoolId) === String(normalizedSchoolId)
    )

    const handleNotificationNew = (payload) => {
      if (!sameSchoolEvent(payload)) return
      const notification = payload?.notification
      if (!getNotificationId(notification)) return
      setNotifications((current) => upsertNotification(current, notification))
    }

    const handleNotificationRead = (payload) => {
      if (!sameSchoolEvent(payload) || !payload?.notificationId || !payload?.userId) return
      setNotifications((current) => markNotificationReadByUser(
        current,
        String(payload.notificationId),
        String(payload.userId),
      ))
    }

    const handleNotificationsReadAll = (payload) => {
      if (!sameSchoolEvent(payload) || String(payload?.userId || '') !== currentUserId) return
      setNotifications((current) => markNotificationsReadByUser(
        current,
        payload?.notificationIds || [],
        currentUserId,
      ))
    }

    const handleNotificationDeleted = (payload) => {
      if (!sameSchoolEvent(payload) || !payload?.notificationId) return
      setNotifications((current) => removeNotificationById(current, String(payload.notificationId)))
    }

    // Notification events are emitted by the backend to the same private user room as messages.
    socket.on('notification:new', handleNotificationNew)
    socket.on('notification:read', handleNotificationRead)
    socket.on('notification:read-all', handleNotificationsReadAll)
    socket.on('notification:deleted', handleNotificationDeleted)

    return () => {
      socket.off('notification:new', handleNotificationNew)
      socket.off('notification:read', handleNotificationRead)
      socket.off('notification:read-all', handleNotificationsReadAll)
      socket.off('notification:deleted', handleNotificationDeleted)
    }
  }, [currentUserId, fetchNotifications, normalizedSchoolId, token])

  useEffect(() => () => {
    disconnectMessageSocket()
  }, [])

  useEffect(() => {
    const handleKeyDown = (event) => {
      const target = event.target
      const tagName = target?.tagName?.toLowerCase()
      const isTypingField = ['input', 'textarea', 'select'].includes(tagName) || target?.isContentEditable

      if (event.key === '/' && !isTypingField) {
        event.preventDefault()
        setQuickOpen(true)
        searchInputRef.current?.focus()
      }

      if (event.key === 'Escape') {
        setQuickOpen(false)
        setNotifOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`fixed lg:static inset-y-0 left-0 z-30 w-64 bg-slate-900 flex flex-col transition-transform duration-300 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        <div className="relative overflow-hidden border-b border-slate-800 flex-shrink-0">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: 'radial-gradient(circle at top left, rgb(var(--color-primary-500) / 0.18), transparent 34%), radial-gradient(circle at bottom right, rgb(var(--color-secondary-500) / 0.12), transparent 28%), linear-gradient(180deg, rgba(15,23,42,0.96), rgba(2,6,23,0.98))',
            }}
          />
          <div className="relative px-4 py-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div
                  title={schoolDisplayName}
                  className="rounded-2xl border border-white/10 bg-white/[0.05] p-3 shadow-lg shadow-slate-950/30 backdrop-blur-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-slate-800 text-sm font-bold text-white shadow-inner shadow-black/20">
                      {schoolLogo ? (
                        <img
                          src={schoolLogo}
                          alt={schoolDisplayName}
                          className="h-full w-full bg-white/95 p-1.5 object-contain"
                          loading="eager"
                          decoding="async"
                        />
                      ) : (
                        <span>{schoolInitials}</span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                          Active School
                        </p>
                        {schoolCodeValue && (
                          <span className="rounded-full border border-white/15 bg-white/10 px-2 py-0.5 text-[10px] font-semibold text-white">
                            {schoolCodeValue}
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-sm font-semibold text-white truncate">
                        {schoolDisplayName}
                      </p>
                      <p className="mt-1 text-xs text-slate-400 truncate">
                        BrainCamp admin operations
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden mt-1 rounded-xl border border-white/10 bg-white/5 p-2 text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-4">
          {visibleSidebarGroups.map(({ label, items }) => {
            const isCollapsed = collapsedGroups[label]

            return (
              <div key={label}>
                <button
                  onClick={() => toggleGroup(label)}
                  className="flex items-center justify-between w-full px-2 mb-1 group"
                >
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider group-hover:text-slate-400 transition-colors">
                    {label}
                  </span>
                  <ChevronDown className={`w-3 h-3 text-slate-600 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                </button>

                {!isCollapsed && (
                  <div className="space-y-0.5">
                    {items.map(({ path, aliases, icon: Icon, label: itemLabel }) => (
                      <NavLink
                        key={path}
                        to={path}
                        onClick={() => setSidebarOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          matchesRoute(location.pathname, path, aliases)
                            ? 'bg-primary-600 text-white shadow-lg shadow-primary-900/30'
                            : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                        }`}
                      >
                        <Icon className="w-[18px] h-[18px] flex-shrink-0" />
                        {itemLabel}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </nav>

        <div className="border-t border-slate-700 p-4 flex-shrink-0">
          <div className="flex items-center gap-3 mb-3">
            <Avatar src={user?.avatar} name={user?.name || 'Admin'} size="md" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate">{user?.name || 'Admin'}</p>
              <p className="text-xs text-slate-400 truncate">{getRoleLabel(user?.role)} • {user?.email || 'admin@school.com'}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-slate-800 hover:text-red-400 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="bg-white/95 backdrop-blur border-b border-gray-100 px-4 lg:px-6 py-4 flex items-center gap-4 flex-shrink-0 shadow-sm shadow-slate-200/40">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-gray-500 hover:text-gray-700 p-1">
            <Menu className="w-5 h-5" />
          </button>

          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h1 className="text-lg font-bold text-gray-900 whitespace-nowrap">{pageTitle}</h1>
              <span
                title={schoolName || schoolConfig?.appName || 'School Admin Workspace'}
                className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-primary-100 bg-primary-50/90 px-3 py-1 text-xs font-semibold text-primary-900 shadow-sm shadow-primary-100/50 min-w-0 max-w-[320px]"
              >
                <Building2 className="w-3.5 h-3.5 text-primary-600 flex-shrink-0" />
                <span className="truncate">{schoolName || schoolConfig?.appName || 'School Admin Workspace'}</span>
              </span>
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
              <span>{schoolConfig?.appName || 'BrainCamp Admin'}</span>
              <ChevronRight className="w-3 h-3" />
              <span className="text-gray-600">{pageTitle}</span>
            </div>
            <p className="text-sm text-gray-500 mt-1 max-w-2xl hidden lg:block">
              {pageDescription}
            </p>
            <p className="md:hidden text-xs font-medium text-primary-700 mt-1 truncate">
              {schoolName || schoolConfig?.appName || 'School Admin Workspace'}
            </p>
          </div>

          <div className="ml-auto flex items-center gap-3">
            <div className="relative hidden sm:block w-72">
              <div className={`flex items-center gap-2 bg-gray-50 border rounded-xl px-3 py-2 transition-all ${
                quickOpen ? 'border-primary-300 shadow-lg shadow-primary-100/50' : 'border-gray-200'
              }`}>
                <Search className="w-4 h-4 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={quickQuery}
                  onChange={(event) => {
                    setQuickQuery(event.target.value)
                    setQuickOpen(true)
                  }}
                  onFocus={() => setQuickOpen(true)}
                  onBlur={() => setTimeout(() => setQuickOpen(false), 120)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && quickResults[0]) {
                      event.preventDefault()
                      handleQuickNavigate(quickResults[0].to)
                    }
                  }}
                  placeholder="Jump to a page..."
                  className="bg-transparent text-sm text-gray-700 placeholder-gray-400 outline-none w-full"
                />
                <span className="rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-gray-400">
                  /
                </span>
              </div>

              {quickOpen && (
                <div className="absolute right-0 top-full mt-2 w-full rounded-2xl border border-gray-100 bg-white shadow-2xl shadow-slate-200/60 overflow-hidden z-50">
                  <div className="px-3 py-2 border-b border-gray-100 bg-gray-50/80">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400">
                      Quick Navigation
                    </p>
                  </div>

                  {quickResults.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-gray-500">
                      No matching pages found.
                    </div>
                  ) : (
                    <div className="max-h-80 overflow-y-auto py-1">
                      {quickResults.map((page) => (
                        <button
                          key={page.to}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => handleQuickNavigate(page.to)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-800">{page.label}</p>
                              <p className="text-xs text-gray-500 mt-0.5 truncate">{page.description}</p>
                            </div>
                            <span className="text-[11px] font-medium text-gray-400 whitespace-nowrap">{page.group}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="relative">
              <button onClick={() => setNotifOpen(!notifOpen)} className="relative p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow-sm">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {notifOpen && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-gray-800 text-sm">Notifications</p>
                        <p className="text-[11px] font-medium text-gray-400 mt-0.5">
                          {notifications.length} total{unreadCount > 0 ? ` • ${unreadCount} unread` : ''}
                        </p>
                      </div>
                      {unreadCount > 0 && (
                        <button
                          type="button"
                          onClick={handleMarkAllNotificationsRead}
                          className="rounded-lg border border-gray-200 p-1.5 text-gray-500 transition-colors hover:border-primary-200 hover:bg-primary-50 hover:text-primary-600"
                          title="Mark all as read"
                        >
                          <CheckCheck className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {notificationsLoading ? (
                      [...Array(3)].map((_, index) => (
                        <div key={index} className="px-4 py-3 border-b border-gray-50 last:border-0 animate-pulse">
                          <div className="flex items-start gap-3">
                            <span className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0 bg-gray-200" />
                            <div className="flex-1 space-y-2">
                              <div className="h-3.5 rounded bg-gray-100 w-4/5" />
                              <div className="h-3 rounded bg-gray-100 w-2/5" />
                            </div>
                          </div>
                        </div>
                      ))
                    ) : recentNotifications.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <p className="text-sm font-medium text-gray-700">No notifications yet</p>
                        <p className="mt-1 text-xs text-gray-400">New school updates will appear here.</p>
                      </div>
                    ) : recentNotifications.map((item) => {
                      const isUnread = !isNotificationReadByUser(item, currentUserId)

                      return (
                        <button
                          type="button"
                          key={item._id || item.id}
                          onClick={() => {
                            setNotifOpen(false)
                            navigate('/notifications')
                          }}
                          className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 border-b border-gray-50 last:border-0 ${
                            isUnread ? 'bg-blue-50/40' : ''
                          } w-full text-left`}
                        >
                          <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${getNotificationAccent(item)}`} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-3">
                              <p className="text-sm text-gray-700 line-clamp-2">{item.title}</p>
                              {isUnread && <span className="mt-1 h-2 w-2 rounded-full bg-primary-500 flex-shrink-0" />}
                            </div>
                            {item.message && (
                              <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.message}</p>
                            )}
                            <p className="text-xs text-gray-400 mt-1.5">
                              {formatNotificationTime(item.createdAt || item.sentAt)}
                            </p>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                  <div className="px-4 py-2 bg-gray-50">
                    <button onClick={() => { setNotifOpen(false); navigate('/notifications') }} className="text-xs text-primary-600 font-semibold w-full text-center">
                      View all notifications
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => navigate('/profile')}
              className="rounded-xl transition-transform hover:scale-[1.02]"
              title="Open my profile"
            >
              <Avatar src={user?.avatar} name={user?.name || 'Admin'} size="md" className="cursor-pointer" />
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-[1600px] p-4 lg:p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
